import { Part, Product, ProductType } from '@inter-ikea-storageone/range';
import { calculateOptimalMountingRails } from '@inter-ikea-storageone/range-boaxel';
import { IProduct } from '@inter-ikea-kompis/types';

export const transformSections = (products: Product[], kompisProducts: IProduct[]): Product[] => {
  const productsClone = JSON.parse(JSON.stringify(products)) as Product[];

  const sections = productsClone.filter(p => p.type === ProductType.BOAXEL_UPRIGHT_SECTION);

  const nonMountingRails = productsClone.filter(
    p => p.type === ProductType.BOAXEL_NON_MOUNTING_RAIL
  );

  const isAdjustableValid = isAdjustableSectionValid(productsClone, kompisProducts);

  const availableMountingRails = products.filter(
    p => p.valid && p.type === ProductType.BOAXEL_MOUNTING_RAIL
  );

  // Invalidate non-mounting-rail if color not present on market.
  const colors = getValidColorsOnMarket(availableMountingRails);
  nonMountingRails.forEach(p => {
    if (!colors.includes(p.design)) p.valid = false;
  });

  sections.forEach(section => {
    if (!section.valid) return;

    // Invalidate adjustable sections if market does not support them.
    if (section.communicatedDimensions.width < 600 && !isAdjustableValid) {
      section.valid = false;
      return;
    }

    const { parts } = section;
    if (!(parts.length === 1)) {
      section.valid = false; // One upright part expected from Dexf data.
      return;
    }

    setUprightParts(section, products);

    // Get all IProducts that match the current sections design & exists as Product
    const matchingIProductMountingRails = getMatchingIProductMountingRails(
      kompisProducts,
      availableMountingRails,
      section
    );

    if (!matchingIProductMountingRails.length) {
      section.valid = false;
      return;
    }

    const optimizedMountingRails = getOptimizedMountingRails(
      matchingIProductMountingRails,
      availableMountingRails,
      section
    );

    optimizedMountingRails.forEach(railProduct => {
      const railPart = convertProductToPart(railProduct);
      parts.push(railPart);
    });
  });

  return productsClone;
};

const isAdjustableSectionValid = (products: Product[], kompisProducts: IProduct[]) => {
  const adjustableProducts = products.filter(p =>
    [ProductType.BOAXEL_ADJ_SHELF, ProductType.BOAXEL_ADJ_CLOTHES_RAIL].includes(p.type)
  );

  adjustableProducts.forEach(p => {
    const IProductVariant = kompisProducts.find(kp => kp.itemId === p.globalItemNo);
    p.valid = IProductVariant?.valid ?? false;
  });

  return adjustableProducts.some(p => p.valid);
};
export const convertProductToPart = (product: Product): Part => ({
  ...product.parts[0],
  product
});

/**
 * Get list of IProduct mounting rails matching the color of provided section
 * @param kompisProducts IProducts to filter from
 * @param availableRails Valid mounting rails of ProductType.BOAXEL_MOUNTING_RAIL
 * @param section Section to match
 * @returns Array of IProducts
 */
const getMatchingIProductMountingRails = (
  kompisProducts: IProduct[],
  availableRails: Product[],
  section: Product
) =>
  kompisProducts.filter(
    p =>
      p.content?.itemNoGlobal &&
      availableRails.find(
        rail => rail.coreProductId === p.content!.itemNoGlobal && rail.design === section.design
      )
  );

/**
 * Get list of optimized mounting rail Products for provided section.
 * @param kompisProducts Array of IProducts to calculate optimal rails from.
 * @param availableRails Array of Products to find corresponding product(s) that match optimized IProducts.
 * @param section Section Product that determines parameters for optimization.
 * @returns An array of Products optimized for given sections length.
 */
const getOptimizedMountingRails = (
  kompisProducts: IProduct[],
  availableRails: Product[],
  section: Product
) => {
  const optimalKompisMountingRails = calculateOptimalMountingRails(
    kompisProducts,
    section.communicatedDimensions.width,
    section.design
  );

  return optimalKompisMountingRails.map(kompisMountingRail => {
    const railProduct = availableRails.find(
      railProduct => railProduct.coreProductId === kompisMountingRail.content?.itemNoGlobal
    );
    if (!railProduct) throw new Error('Could not find mounting rail product.');
    return railProduct;
  });
};

/**
 * Get list of unique colors on market
 * @param products Available products on market
 * @returns String[] Array of colors on market
 */
const getValidColorsOnMarket = (products: Product[]) =>
  products.reduce((acc: string[], curr) => {
    if (acc.includes(curr.design)) return acc;
    acc.push(curr.design);
    return acc;
  }, []);

/**
 * Replaces the existing upright part with parts data including product data.
 * @param section Section to update
 * @param products Available products
 * @returns void
 */
const setUprightParts = (section: Product, products: Product[]) => {
  const { parts } = section;

  const [existingPart] = parts;
  const uprightProduct = products.find(
    p => p.ruItemNo?.includes(existingPart.localId) && p.type !== ProductType.BOAXEL_UPRIGHT_SECTION
  );

  if (!uprightProduct) return;

  const uprightParts = [convertProductToPart(uprightProduct), convertProductToPart(uprightProduct)];

  parts.pop();
  parts.push(...uprightParts);
};
